/**
 * Halloween sale graphics — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenSaleGraphicsPack: ToilePack = {
    slug: 'halloween-sale-graphics',
    category: 'season',
    name: 'Halloween sale graphics',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'percent', label: 'Percent off', subject: 'A square graphic lettered "SALE" large with a blank circle for the percentage and bats around it'},
        {key: 'burst', label: 'Candy burst', subject: 'A square graphic of a candy bucket spilling sweets with a burst behind, lettered "SPOOKY DEALS"'},
        {key: 'ribbon', label: 'Ribbon banner', subject: 'A square graphic with a black ribbon banner lettered "HALLOWEEN SALE" over a web pattern'},
    ],
    styles: [
        {key: 'cute', label: 'Spooky cute', style: 'kawaii spooky illustration, rounded shapes, soft outlines, friendly faces', palette: 'Pastel purple, orange, mint and cream with black accents', framing: 'Centered on a solid background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: '1950s halloween paper decoration style, halftone grain, slight misregistration', palette: 'Orange, black, cream and a faded green', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'bold', label: 'Bold graphic', style: 'bold graphic illustration, thick outlines, high contrast, flat fills', palette: 'Orange, black and white with a single purple', framing: 'Centered on a solid background with a little room around it'},
    ],
    directions: ['for Black Friday instead', 'as a countdown timer', 'with free shipping text'],
    directionHint: 'try: for Black Friday instead',
    seo: {
        title: 'Halloween Sale Graphics: Percent-Off & Candy Banners',
        metaDescription: 'Nine Halloween sale graphics — percent-off callout, candy burst, ribbon banner — in spooky-cute, vintage, and bold graphic styles.',
        h1: 'Halloween sale graphic designs: percent-off, candy burst, ribbon banner',
        tagline: 'Nine sale graphics, three offers, three moods each.',
        intro: 'Nine ready-to-letter sale graphics for the Halloween rush: a blank percent-off callout, a candy-burst deal square, and a ribbon banner reading Halloween Sale — each in a spooky-cute, a 1950s vintage, and a bold graphic treatment. Drop in your own discount number and the design carries the rest.',
        useCases: [
            "a shop's storefront window sign",
            'an Etsy or Shopify sale banner',
            'a social media discount post',
            'an email header for a Halloween promotion',
        ],
        faq: [
            {q: 'What format do the sale graphics come in?', a: 'Nine square image files, one per offer-and-style combination, sized to drop into a banner, post, or print template.'},
            {q: 'Can I add my own discount percentage?', a: 'Yes — the percent-off design leaves the number blank so you can add your own figure in any image editor.'},
            {q: 'Do these print as well as they post online?', a: 'Yes — the ribbon banner and candy-burst designs print cleanly at poster size for a window or counter, and drop in at social size for a feed post.'},
            {q: 'Can I use these for a paid ad or a physical storefront?', a: 'Yes — the designs are yours to use commercially, including paid social, print, and in-store signage.'},
        ],
    },
}
